import React, {useEffect, useState} from "react";
import {Card} from "react-bootstrap";
import {getAuth, updatePassword} from "@firebase/auth";
import {doc, setDoc} from "@firebase/firestore";
import {db} from "../../firebase/FirebaseProvider";
import {Location, SettingsDoc, User} from "../types/Types";
import PageTracking from "../auth/PageTracking";
import UserBadgesCard from "./UserBadgesCard";

interface Props {
    currentUser: User | undefined;
    locations: Array<Location>;
    settings: SettingsDoc | undefined;
}

function Settings(props: Props) {

    const [password, setPassword] = useState<string>();
    const [feedback, setFeedback] = useState<string>();
    const [passwordStatus, setPasswordStatus] = useState<number>(-1);
    const [username, setUsername] = useState<string>('');
    const [usernameFeedback, setUsernameFeedback] = useState<string>();
    const [usernameStatus, setUsernameStatus] = useState<number>(-1);

    useEffect(() => {
        setUsername(props.currentUser?.name || '');
    }, [props.currentUser?.name]);

    const savePassword = async () => {
        const auth = getAuth();
        const user = auth.currentUser;
        if(user && password) {
            updatePassword(user, password).then(() => {
                setFeedback("Password successfully saved.")
                setPasswordStatus(2);
            }).catch((error) => {
                if(error.message.includes("requires-recent-login")) {
                    setFeedback("To set a password you need to have logged in more recently, please log out and log back in, and then try again.");
                    setPasswordStatus(1);
                } else {
                    setFeedback(error.message);
                    setPasswordStatus(1);
                }
            });
        }
    };

    const isValidPassword = (): boolean => {
        return password != null && password.length > 5;
    };

    const saveUsername = async () => {
        if(!props.currentUser || !isValidUsername()) {
            return;
        }

        const usersRef = doc(db, 'users', props.currentUser.id);
        setDoc(usersRef, {
            name: username.trim()
        }, {merge: true}).then(() => {
            setUsernameFeedback("Username successfully saved.");
            setUsernameStatus(2);
        }).catch((error) => {
            setUsernameFeedback(error.message);
            setUsernameStatus(1);
        });
    };

    const isValidUsername = (): boolean => {
        return username.trim().length > 0 && username.trim() !== props.currentUser?.name;
    };

    const toggleSocial = async(toggleState: boolean) => {
        if(!props.currentUser) {
            return;
        }
        const usersRef = doc(db, 'users', props.currentUser.id);
        await setDoc(usersRef, {
            socialDisabled: toggleState
        }, {merge: true});
    }

    const toggleBadge =  async(toggleState: boolean) => {
        if(!props.currentUser) {
            return;
        }
        const usersRef = doc(db, 'users', props.currentUser.id);
        await setDoc(usersRef, {
            badgeDisabled: toggleState
        }, {merge: true});
    };

    const toggleNotifications =  async(toggleState: boolean) => {
        if(!props.currentUser) {
            return;
        }
        const usersRef = doc(db, 'users', props.currentUser.id);
        await setDoc(usersRef, {
            chatPushNotificationsEnabled: toggleState
        }, {merge: true});
    };

    const toggleClosedPubsVisibility = async(toggleState: boolean) => {
        if(!props.currentUser) {
            return;
        }
        const usersRef = doc(db, 'users', props.currentUser.id);
        await setDoc(usersRef, {
            hideVisitedClosedPubsOnMap : toggleState
        }, { merge: true });
    };

    const toggleUiV2 = async(toggleState: boolean) => {
        if(!props.currentUser) {
            return;
        }
        const usersRef = doc(db, 'users', props.currentUser.id);
        await setDoc(usersRef, {
            enableUiV2: toggleState
        }, { merge: true });
    };

    return (
        <>
            <PageTracking />
            {props.currentUser && <>
            {props.currentUser.badges && props.currentUser.badges.length > 0 && (
                <UserBadgesCard badges={props.currentUser.badges} />
            )}
            <>
                <Card className={"mb-3"}>
                    <Card.Header>
                        <div>Password</div>
                    </Card.Header>
                    <Card.Body>
                        <div>If you would like to set a password for your account please do so below. To sign in using a password please enter it on the login screen.</div>
                        <div className={"mb-3"}>Your password should have a minimum of 6 characters.</div>
                        <div className={"col-12 col-md-8 col-lg-6 pr-0 pl-0"}>
                            <div className="input-group mb-3">
                                <div className="input-group-prepend">
                                    <span className="input-group-text" id="inputGroup-sizing-default">Password</span>
                                </div>
                                <input type="password"
                                       name={"password"}
                                       onChange={e => setPassword(e.target.value)}
                                       className="form-control w-50"
                                       aria-label="Default" aria-describedby="inputGroup-sizing-default" />
                            </div>
                            {passwordStatus > 0 && <div>
                                {passwordStatus === 2 && <div className={"alert alert-success p-2"}>
                                    <span>{feedback}</span>
                                </div>}
                                {passwordStatus === 1 && <div className={"alert alert-danger p-2"}>
                                    <span>{feedback}</span>
                                </div>}
                            </div>}
                        </div>
                        <div className={"float-right"}>
                            <button disabled={!isValidPassword()} className={"btn btn-primary"} onClick={() => {savePassword()}}>Save</button>
                        </div>
                    </Card.Body>
                </Card>
            </>
            <>
                <Card className={"mb-3"}>
                    <Card.Header>
                        <div>Username</div>
                    </Card.Header>
                    <Card.Body>
                        <div className={"mb-3"}>Update the username shown on your profile and around the site.</div>
                        <div className={"col-12 col-md-8 col-lg-6 pr-0 pl-0"}>
                            <div className="input-group mb-3">
                                <div className="input-group-prepend">
                                    <span className="input-group-text" id="inputGroup-sizing-username">Username</span>
                                </div>
                                <input type="text"
                                       name={"username"}
                                       value={username}
                                       onChange={e => {
                                           setUsername(e.target.value);
                                           setUsernameStatus(-1);
                                       }}
                                       className="form-control w-50"
                                       aria-label="Username" aria-describedby="inputGroup-sizing-username" />
                            </div>
                            {usernameStatus > 0 && <div>
                                {usernameStatus === 2 && <div className={"alert alert-success p-2"}>
                                    <span>{usernameFeedback}</span>
                                </div>}
                                {usernameStatus === 1 && <div className={"alert alert-danger p-2"}>
                                    <span>{usernameFeedback}</span>
                                </div>}
                            </div>}
                        </div>
                        <div className={"float-right"}>
                            <button disabled={!isValidUsername()} className={"btn btn-primary"} onClick={() => {saveUsername()}}>Save</button>
                        </div>
                    </Card.Body>
                </Card>
            </>
                <Card className={"mb-3"}>
                    <Card.Header>
                        <div>Enable User Interface Version 2</div>
                    </Card.Header>
                    <Card.Body>
                        <div>
                            <div>User Interface Version 2 is switched
                                <b className={"ml-1"}>
                                    {props.currentUser.enableUiV2 && <span>ON</span>}
                                    {!props.currentUser.enableUiV2 && <span>OFF</span>}
                                </b>
                                .
                            </div>
                        </div>
                        <div className={"float-right"}>
                            {!props.currentUser.enableUiV2 && <button className={"btn btn-primary"} onClick={() => { toggleUiV2(true) }}>Enable</button>}
                            {props.currentUser.enableUiV2 && <button className={"btn btn-secondary"} onClick={() => { toggleUiV2(false) }}>Disable</button>}
                        </div>
                    </Card.Body>
                </Card>
                <>
                    <Card className={"mb-3"}>
                        <Card.Header>
                            <div>Hide visited closed pubs on the map</div>
                        </Card.Header>
                        <Card.Body>
                            <div>
                                <div>Hide visited closed pubs on the map is switched
                                    <b className={"ml-1"}>
                                        {props.currentUser.hideVisitedClosedPubsOnMap && <span>ON</span>}
                                        {!props.currentUser.hideVisitedClosedPubsOnMap && <span>OFF</span>}
                                    </b>
                                    .
                                </div>
                            </div>
                            <div className={"float-right"}>
                                {!props.currentUser.hideVisitedClosedPubsOnMap && <button className={"btn btn-primary"} onClick={() => { toggleClosedPubsVisibility(true) }}>Enable</button>}
                                {props.currentUser.hideVisitedClosedPubsOnMap && <button className={"btn btn-secondary"} onClick={() => { toggleClosedPubsVisibility(false) }}>Disable</button>}
                            </div>
                        </Card.Body>
                    </Card>
                </>
            {/* {isChatEnabled(props.currentUser, props.settings) && <>
                <Card className={"mb-3"}>
                    <Card.Header>
                        <div>Enable chat notifications</div>
                    </Card.Header>
                    <Card.Body>
                        <div>
                            <div>Chat notifications are switched
                                <b className={"ml-1"}>
                                    {props.currentUser.chatPushNotificationsEnabled && <span>ON</span>}
                                    {!props.currentUser.chatPushNotificationsEnabled && <span>OFF</span>}
                                </b>
                                .
                            </div>
                            {!props.currentUser.chatPushNotificationsEnabled && <div>Switch on to enable email notifications of new chat messages.</div>}
                        </div>
                        {props.currentUser && <div className={"float-right"}>
                            {!props.currentUser.chatPushNotificationsEnabled && <button className={"btn btn-primary"} onClick={() => {toggleNotifications(true)}}>Enable</button>}
                            {props.currentUser.chatPushNotificationsEnabled && <button className={"btn btn-secondary"} onClick={() => {toggleNotifications(false)}}>Disable</button>}
                        </div>}
                    </Card.Body>
                </Card>
            </>} */}
            <>
                <Card className={"mb-3"}>
                    <Card.Header>
                        <div>Disable social features</div>
                    </Card.Header>
                    <Card.Body>
                        {!props.currentUser.socialDisabled && <div className={"mb-2"}>
                            <div>Your social participation setting is switched <b>ON</b>.</div>
                            <div>If you would like to disable participation in the social features of the site, please click the button below.</div>
                        </div>}
                        {props.currentUser.socialDisabled && <div className={"mb-2"}>
                            <div>Your social participation setting is switched <b>OFF</b>.</div>
                            <div>If you would like to participate in the social features of the site, please click the button below.</div>
                        </div>}
                        {props.currentUser && <div className={"float-right"}>
                            {props.currentUser.socialDisabled && <button className={"btn btn-primary"} onClick={() => {toggleSocial(false)}}>Re-enable</button>}
                            {!props.currentUser.socialDisabled && <button className={"btn btn-secondary"} onClick={() => {toggleSocial(true)}}>Disable</button>}
                        </div>}
                    </Card.Body>
                </Card>
            </>
            {props.currentUser && props.currentUser.countUpdateEnabled && <>
                {props.locations.map((location: Location) => (
                    <div>http://localhost:3000/location/{location.id}/comments</div>
                ))
                }
            </>}
        </>}
        </>
    )
}

export default Settings;
